/**
 * Itens incluídos pelo fornecedor durante a montagem da proposta (mode=orcamento).
 *
 * Diferente das substituições (que trocam um item por outro), uma "adição" cria
 * um item novo dentro de uma categoria do cardápio. O custo é sempre por
 * convidado (regra de negócio acordada) e nunca ocupa uma vaga de `maxChoices`.
 */
export interface MenuAddition {
  /** Identificador estável, usado como key de render e para remoção. */
  id: string;
  /** Nome exibido do item incluído. */
  name: string;
  /** Custo adicional por convidado. Zero significa item sem custo. */
  costPerGuest: number;
  /** Origem: item já cadastrado no cardápio do fornecedor ou texto livre. */
  source: 'catalog' | 'custom';
}

/** categoryId -> lista de itens incluídos naquela categoria */
export type MenuAdditionsMap = Record<string, MenuAddition[]>;

const isAddition = (value: unknown): value is MenuAddition => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<MenuAddition>;
  return typeof candidate.name === 'string' && candidate.name.trim().length > 0;
};

/**
 * Normaliza dados vindos do banco/sessionStorage (jsonb livre) para o formato
 * tipado. Entradas inválidas são descartadas silenciosamente.
 */
export const normalizeAdditions = (value: unknown): MenuAdditionsMap => {
  if (!value) return {};

  let parsed: unknown = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value);
    } catch {
      return {};
    }
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

  const result: MenuAdditionsMap = {};
  Object.entries(parsed as Record<string, unknown>).forEach(([categoryId, list]) => {
    if (!Array.isArray(list)) return;
    const items = list.filter(isAddition).map((item, index) => ({
      id: String((item as MenuAddition).id || `${categoryId}-add-${index}`),
      name: String(item.name).trim(),
      costPerGuest: Number((item as MenuAddition).costPerGuest) || 0,
      source: (item as MenuAddition).source === 'catalog' ? 'catalog' as const : 'custom' as const,
    }));
    if (items.length > 0) result[categoryId] = items;
  });

  return result;
};

/** Soma o custo por convidado de todos os itens incluídos. */
export const calculateAdditionsCost = (additions?: MenuAdditionsMap | null): number => {
  if (!additions) return 0;
  return Object.values(additions).reduce(
    (sum, list) => sum + (Array.isArray(list) ? list.reduce((acc, item) => acc + (Number(item.costPerGuest) || 0), 0) : 0),
    0,
  );
};

/** True quando existe pelo menos um item incluído. */
export const hasAdditions = (additions?: MenuAdditionsMap | null): boolean =>
  !!additions && Object.values(additions).some((list) => Array.isArray(list) && list.length > 0);

/** Lista achatada, útil para carrinho, contrato e resumos. */
export const flattenAdditions = (additions?: MenuAdditionsMap | null): MenuAddition[] => {
  if (!additions) return [];
  return Object.values(additions).flatMap((list) => (Array.isArray(list) ? list : []));
};

/**
 * Itens incluídos que vão no carrinho quando o autosave da proposta grava a seleção. O valor FRESCO do evento de autosave tem
 * prioridade (vazio = o fornecedor acabou de remover o último); sem ele, vale o estado da tela. Sem itens, `undefined`.
 * Antes o item do carrinho nascia sem `additions` e a releitura do carrinho apagava o item que o fornecedor acabou de incluir
 * (e o custo por convidado dele), no mesmo instante em que ele aparecia na lista.
 */
export const additionsForCart = (fresh: unknown, current?: MenuAdditionsMap | null): MenuAdditionsMap | undefined => {
  const source = fresh !== undefined && fresh !== null ? normalizeAdditions(fresh) : normalizeAdditions(current);
  return hasAdditions(source) ? source : undefined;
};
